import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/table";
import AddUser from "./AddUser";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "store/userSlice";
import UserActionMenu from "./UserActionMenu";
import DeleteUserModal from "./DeleteUserModal";
import EditUser from "./EditUser";
import PasswordUserModal from "./PasswordUserModal";

const columnHelper = createColumnHelper();

export default function CheckTable() {
  const dispatch = useDispatch();
  const { data, error, loading } = useSelector((state) => state.users);
  const { user = {} } = useSelector((state) => state.auth.permissions || {});

  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };
  const handlePassword = (user) => {
    setSelectedUser(user);
    setIsPasswordModalOpen(true);
  };

  const handleDelete = async (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const refreshUserList = () => {
    setRefreshTrigger((prev) => !prev);
  };
  useEffect(() => {
    const page = pagination.pageIndex + 1;
    const limit = pagination.pageSize;

    dispatch(getUsers({ page, limit, search: searchText }));
  }, [
    dispatch,
    pagination.pageIndex,
    pagination.pageSize,
    searchText,
    refreshTrigger,
  ]);
  const totalPages = useMemo(() => {
    const totalCount = data?.total ?? 0;
    const pageSize = pagination.pageSize;
    if (!pageSize || pageSize <= 0) return 0;
    return Math.ceil(totalCount / pageSize);
  }, [data?.total, pagination.pageSize]);

  const columns = React.useMemo(
    () => [
      // Name (firstname + lastname)
      columnHelper.accessor(
        (row) => [`${row.firstname} ${row.lastname}`, row.isActive],
        {
          id: "name",
          header: "NAME",
          enableHiding: false,
          cell: (info) => info.getValue()[0],
        }
      ),

      // User Type
      columnHelper.accessor((row) => row.role?.name, {
        id: "roleName", // unique column id
        header: "USER TYPE",
        enableHiding: false,
        cell: (info) => info.getValue() || "-", // fallback if role or name is missing
      }),

      // Email
      columnHelper.accessor("email", {
        id: "email",
        header: "EMAIL",
        enableHiding: false,

        cell: (info) => info.getValue(),
      }),

      // Mobile
      columnHelper.accessor("mobile", {
        id: "mobile",
        header: "MOBILE",
        enableHiding: false,

        cell: (info) => info.getValue(),
      }),

      // Created At
      columnHelper.accessor("createdAt", {
        id: "createdAt",
        header: "CREATED AT",
        cell: (info) => {
          const date = new Date(info.getValue());
          return isNaN(date)
            ? info.getValue()
            : date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
        },
      }),

      // Updated At
      columnHelper.accessor("updatedAt", {
        id: "updatedAt",
        header: "UPDATED AT",
        cell: (info) => {
          const date = new Date(info.getValue());
          return isNaN(date)
            ? info.getValue()
            : date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
        },
      }),
      columnHelper.display({
        id: "action",
        header: "ACTIONS",
        enableHiding: false,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <UserActionMenu
              user={user}
              onEdit={handleEdit}
              onPassword={handlePassword}
              onDelete={handleDelete}
            />
          );
        },
      }),
    ],
    []
  );

  const usersData = useMemo(() => data?.users ?? [], [data]);
  const initialColumnVisibility = {
    name: true,
    roleName: true,
    email: true,
    mobile: true,
    createdAt: false,
    updatedAt: false,
    action: true,
  };
  return (
    <>
      {isEditModalOpen && selectedUser && (
        <EditUser
          userData={selectedUser}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          refreshUserList={refreshUserList}
        />
      )}
      {isPasswordModalOpen && selectedUser && (
        <PasswordUserModal
          userData={selectedUser}
          isOpen={isPasswordModalOpen}
          onClose={() => {
            setIsPasswordModalOpen(false);
            setSelectedUser(null);
          }}
          refreshUserList={refreshUserList}
        />
      )}
      {isDeleteModalOpen && selectedUser && (
        <DeleteUserModal
          userData={selectedUser}
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          refreshUserList={refreshUserList}
        />
      )}
      <DataTable
        title="Users"
        columns={columns}
        data={usersData ?? []}
        pageSize={5}
        error={error}
        loading={loading}
        totalPages={totalPages}
        total={data?.total}
        pagination={pagination}
        setPagination={setPagination}
        isColumnDropdown={true}
        initialColumnVisibility={initialColumnVisibility}
        searchText={searchText}
        setSearchText={setSearchText}
        searchPlaceholder={"Search User"}
        rightAccessory={
          user?.create && <AddUser refreshUserList={refreshUserList} />
        }
      />
    </>
  );
}
